import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import ApexCharts from 'apexcharts';
import { Box, useColorMode } from '@chakra-ui/react';
import { DataTableHeader } from './DataTableHeader';
import { DataTableFooter } from './DataTableFooter';
import { ChartTileFlex } from '../../theme/ChartTileFlex';
import { CustomSpinner } from './CustomSpinner';

export const BitcoinBlockTimeChart = ({
  uniqueChartId,
  title,
  subtitle,
  icon,
  link,
  linkLabel = 'Learn More',
  data,
  formatNumber,
  formatLargeNumber
}) => {
  const [chartData, setChartData] = useState([]);
  const chartRef = useRef(null);
  const chartInstance = useRef(null);
  const [loading, setLoading] = useState(true);

  const { colorMode } = useColorMode();
  const gridColor = colorMode === 'dark' ? '#555555' : '#CCCCCC';
  const labelColor = colorMode === 'dark' ? '#999' : '#666';
  const legendTextColor = colorMode === 'dark' ? '#FFFFFF' : '#121212';

  const processData = useCallback((data) => {
    if (!data?.ts08?.mining_fees || !Array.isArray(data.ts08.mining_fees)) {
      return [];
    }

    const miningFees = data.ts08.mining_fees;
    // Get current difficulty for hashrate calculation
    const difficulty = data?.s03?.bitcoin_stats?.bitcoin_data?.difficulty || 0;
    
    // For 7 days, aggregate data by day
    const sevenDaysAgo = Math.floor(Date.now() / 1000) - (7 * 24 * 60 * 60);
    
    // Filter data from last 7 days
    let dataPoints = miningFees.filter(point => point.timestamp >= sevenDaysAgo);
    
    // If we have too many points, aggregate by day
    if (dataPoints.length > 7) {
      const dailyData = {};
      
      dataPoints.forEach(point => {
        const date = new Date(point.timestamp * 1000).toDateString();
        if (!dailyData[date]) {
          dailyData[date] = [];
        }
        dailyData[date].push(point);
      });
      
      // Calculate daily averages
      dataPoints = Object.keys(dailyData).map(date => {
        const dayPoints = dailyData[date];
        const avgTimestamp = dayPoints.reduce((sum, p) => sum + p.timestamp, 0) / dayPoints.length;
        const avgHeight = dayPoints.reduce((sum, p) => sum + p.avgHeight, 0) / dayPoints.length;
        return {
          timestamp: avgTimestamp,
          avgHeight: avgHeight,
          avgFees: dayPoints[0].avgFees
        };
      }).sort((a, b) => a.timestamp - b.timestamp);
    }

    // Calculate block times and estimated hash rates
    return dataPoints.map((point, index) => {
      const prevPoint = index > 0 ? dataPoints[index - 1] : null;
      const blockInterval = prevPoint 
        ? ((point.timestamp - prevPoint.timestamp) / 60) / (point.avgHeight - prevPoint.avgHeight)
        : 10; // Default to 10 minutes if no previous point

      // Calculate hashrate from difficulty and block time
      // Hashrate (H/s) = (Difficulty × 2^32) / (Block Time in seconds)
      // Convert to EH/s by dividing by 10^18
      const blockTimeSeconds = blockInterval * 60; // Convert minutes to seconds
      const hashRate = difficulty > 0 && blockTimeSeconds > 0
        ? (difficulty * Math.pow(2, 32)) / blockTimeSeconds / 1e18
        : 0;

      return {
        x: new Date(point.timestamp * 1000).toLocaleDateString('en-US', { 
          month: 'short', 
          day: 'numeric' 
        }),
        timestamp: point.timestamp,
        blockInterval: blockInterval,
        height: point.avgHeight,
        hashRate: hashRate
      };
    });
  }, []);

  useEffect(() => {
    if (data) {
      const processedData = processData(data);
      setChartData(processedData);
      setLoading(false);
    }
  }, [data, processData]);

  const chartOptions = useMemo(() => {
    return {
      chart: {
        type: 'line',
        height: 350,
        toolbar: { show: false },
        zoom: { enabled: false },
        animations: { enabled: true },
        background: 'transparent'
      },
      stroke: {
        curve: 'smooth',
        width: [3, 3],
        dashArray: [0, 0]
      },
      markers: { 
        size: [4, 4],
        hover: {
          size: [6, 6]
        }
      },
      annotations: {
        yaxis: [
          {
            y: 10,
            borderColor: '#775DD0',
            strokeDashArray: 5,
            label: {
              borderColor: '#775DD0',
              style: {
                color: '#fff',
                background: '#775DD0'
              },
              text: '10m Target'
            }
          }
        ]
      },
      xaxis: {
        type: 'category',
        categories: chartData.map(item => item.x),
        title: {
          text: 'Date',
          style: { color: labelColor },
        },
        labels: {
          style: { colors: labelColor },
          rotate: -45,
          rotateAlways: false,
          hideOverlappingLabels: true
        },
      },
      yaxis: [
        {
          title: {
            text: 'Block Time (minutes)',
            style: { color: labelColor },
          },
          labels: {
            formatter: (value) => formatNumber(value, 0),
            style: { colors: labelColor },
          },
          min: 0,
          max: 25,
          tickAmount: 5,
        },
        {
          opposite: true,
          title: {
            text: 'Network Hash Rate',
            style: { color: labelColor },
          },
          labels: {
            formatter: (value) => {
              // Handle EH/s and above
              if (value >= 1000) {
                return formatNumber(value / 1000, 2) + ' ZH/s';
              }
              return formatNumber(value, 0) + ' EH/s';
            },
            style: { colors: labelColor },
          },
        }
      ],
      tooltip: {
        shared: true,
        intersect: false,
        theme: colorMode,
        y: {
          formatter: function (value, { seriesIndex }) {
            if (seriesIndex === 0) return formatNumber(value, 2) + ' minutes';
            if (seriesIndex === 1) {
              if (value >= 1000) {
                return formatNumber(value / 1000, 2) + ' ZH/s';
              }
              return formatNumber(value, 2) + ' EH/s';
            }
          }
        },
        custom: function({ series, seriesIndex, dataPointIndex, w }) {
          const data = chartData[dataPointIndex];
          return '<div class="custom-tooltip" style="padding: 10px; background: ' + 
            (colorMode === 'dark' ? '#2D3748' : '#ffffff') + 
            '; color: ' + (colorMode === 'dark' ? '#ffffff' : '#000000') + ';">' +
            '<div><strong>Date:</strong> ' + data.x + '</div>' +
            '<div><strong>Block Time:</strong> ' + formatNumber(data.blockInterval, 2) + ' min</div>' +
            '<div><strong>Hash Rate:</strong> ' + (data.hashRate >= 1000 ? formatNumber(data.hashRate / 1000, 2) + ' ZH/s' : formatNumber(data.hashRate, 2) + ' EH/s') + '</div>' +
            '</div>';
        }
      },
      legend: { 
        position: 'top',
        labels: {
          colors: legendTextColor
        },
        markers: {
          width: 12,
          height: 12,
          strokeWidth: 0,
          strokeColor: '#fff',
          fillColors: undefined,
          radius: 12,
          customHTML: undefined,
          onClick: undefined,
          offsetX: 0,
          offsetY: 0
        },
        itemMargin: {
          horizontal: 10,
          vertical: 0
        }
      },
      grid: { borderColor: gridColor },
      colors: ["#f7931a", "#3B82F6"],
    };
  }, [colorMode, gridColor, labelColor, legendTextColor, formatNumber, chartData]);

  useEffect(() => {
    if (chartData.length > 0 && chartRef.current) {
      const series = [
        {
          name: 'Block Time',
          data: chartData.map(item => item.blockInterval),
          type: 'line'
        },
        {
          name: 'Hash Rate',
          data: chartData.map(item => item.hashRate),
          yAxisIndex: 1,
          type: 'line'
        }
      ];

      if (chartInstance.current) {
        chartInstance.current.updateOptions({
          ...chartOptions,
          series: series
        });
      } else {
        chartInstance.current = new ApexCharts(chartRef.current, {
          ...chartOptions,
          series: series
        });
        chartInstance.current.render();
      }
    }

    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
        chartInstance.current = null;
      }
    };
  }, [chartData, chartOptions, formatNumber]);

  return (
    <ChartTileFlex
      direction="column"
      mb="12px"
      py="18px"
      flex={1}
      borderRadius={3}
      style={{ minHeight: '337px', maxHeight: '100%' }}
    >
      <DataTableHeader title={title} subtitle={subtitle} icon={icon} />
      <Box flexGrow={1} minHeight='337px' width="100%">
        {loading ? <CustomSpinner /> : <div ref={chartRef} className={`chart-container-${uniqueChartId}`} style={{ height: '100%' }} />}
      </Box>
      <DataTableFooter link={link} linkLabel={linkLabel} />
    </ChartTileFlex>
  );
};